import { getLang, resolvedLang } from "@/lib/lang";
import { isStudioPreview } from "@/lib/studioPreviewBridge";

// Page de confirmation d'une demande de contact — ce qui est partagé par
// TOUS les moteurs de thème : l'URL, le jeton de navigation qui en autorise
// l'affichage, et les textes fixes de la page.
//
// Pourquoi un jeton dans l'état de navigation (history.state) plutôt qu'un
// paramètre d'URL : la page ne doit s'afficher QU'APRÈS un envoi réussi.
// Un « ?envoye=1 » se taperait à la main ou se partagerait ; l'état de
// navigation, lui, n'est posé que par ContactModalProvider.notifySent(),
// c'est-à-dire après la réponse OK de /contact-request. Ouverte directement,
// l'URL n'a aucun état et renvoie à l'accueil. L'état est propre à l'origine,
// donc à la boutique (résolue par nom de domaine) : un envoi sur une boutique
// n'ouvre jamais la confirmation d'une autre.
export const CONTACT_CONFIRMATION_PATH = "/contact/confirmation";

const STATE_KEY = "contactConfirmation";

/**
 * Cible de navigation vers la page de confirmation. Les paramètres d'URL qui
 * doivent survivre au changement de page y sont reportés (voir langSearch).
 */
export function confirmationTarget({ message = "", name = "" } = {}) {
  return {
    to: { pathname: CONTACT_CONFIRMATION_PATH, search: langSearch() },
    state: { [STATE_KEY]: { message, name, sentAt: Date.now() } },
  };
}

/** Jeton posé par un envoi réussi, ou null (accès direct à l'URL). */
export function readConfirmation(locationState) {
  const data = locationState?.[STATE_KEY];
  return data && typeof data.sentAt === "number" ? data : null;
}

/**
 * Paramètres d'URL à conserver en quittant la page courante (vers la
 * confirmation, puis au retour à l'accueil).
 *
 * - Site public : la langue demandée. Un visiteur arrivé par un lien
 *   « ?lang=ar » sans choix mémorisé retomberait sinon sur le français.
 * - Aperçu du Studio de thème : TOUTE la chaîne de requête
 *   (studio_preview, theme_id, preview_token, admin_origin…). Sans elle, la
 *   page de confirmation affichée dans le Canvas perdrait le thème en cours
 *   d'édition et le lien avec l'éditeur.
 */
export function langSearch() {
  if (isStudioPreview()) return window.location.search;
  const lang = getLang();
  return lang ? `?lang=${lang}` : "";
}

// Textes fixes de la page, dans les trois langues du module multilingue
// (voir DIRECTIONS dans lib/lang.js). Le message principal, lui, reste
// celui que la boutique a saisi dans son thème quand le formulaire le
// fournit — ces textes ne sont que le cadre et le repli.
const COPY = {
  fr: {
    eyebrow: "Demande envoyée",
    title: "Merci",
    titleNamed: (name) => `Merci ${name}`,
    titleAfter: ", votre demande est bien arrivée.",
    defaultMessage: "Nous l'avons bien reçue et revenons vers vous dans les plus brefs délais.",
    stepsTitle: "Et maintenant ?",
    steps: [
      { title: "Demande reçue", text: "Votre message est enregistré chez nous." },
      { title: "Étude de votre besoin", text: "Nous prenons connaissance de votre demande." },
      { title: "Notre réponse", text: "Nous vous recontactons par e-mail ou par téléphone." },
    ],
    stepDone: "Fait",
    urgent: "Une question urgente ?",
    home: "Retour à l'accueil",
    seoTitle: (shop) => `Demande envoyée | ${shop}`,
  },
  en: {
    eyebrow: "Request sent",
    title: "Thank you",
    titleNamed: (name) => `Thank you ${name}`,
    titleAfter: ", your request has reached us.",
    defaultMessage: "We have received it and will get back to you as soon as possible.",
    stepsTitle: "What happens next?",
    steps: [
      { title: "Request received", text: "Your message is safely recorded." },
      { title: "Reviewing your needs", text: "We read through your request." },
      { title: "Our reply", text: "We get back to you by email or phone." },
    ],
    stepDone: "Done",
    urgent: "Urgent question?",
    home: "Back to home",
    seoTitle: (shop) => `Request sent | ${shop}`,
  },
  ar: {
    eyebrow: "تم إرسال الطلب",
    title: "شكرًا",
    titleNamed: (name) => `شكرًا ${name}`,
    titleAfter: "، لقد وصلنا طلبك بنجاح.",
    defaultMessage: "لقد استلمنا طلبك وسنعود إليك في أقرب وقت ممكن.",
    stepsTitle: "ماذا بعد؟",
    steps: [
      { title: "تم استلام الطلب", text: "رسالتك مسجلة لدينا." },
      { title: "دراسة احتياجك", text: "نطّلع على تفاصيل طلبك." },
      { title: "ردّنا", text: "نتواصل معك عبر البريد الإلكتروني أو الهاتف." },
    ],
    stepDone: "تم",
    urgent: "سؤال عاجل؟",
    home: "العودة إلى الرئيسية",
    seoTitle: (shop) => `تم إرسال الطلب | ${shop}`,
  },
};

export function confirmationCopy() {
  return COPY[resolvedLang()] || COPY.fr;
}
